import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { EVENT_STATUS_META, getEventStatus } from './eventStatus';
import type { EventSummary } from '../../services/eventService';

type EventCalendarProps = Readonly<{
  events: ReadonlyArray<EventSummary>;
  onSelect: (event: EventSummary) => void;
}>;

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function moveMonth(year: number, month: number, direction: -1 | 1) {
  const nextDate = new Date(year, month + direction, 1);

  return {
    year: nextDate.getFullYear(),
    month: nextDate.getMonth(),
  };
}

function getWeekdayColor(weekday: number) {
  if (weekday === 0) {
    return '#dc2626';
  }

  if (weekday === 6) {
    return '#2563eb';
  }

  return '#64748b';
}

function getDateTextColor(isToday: boolean, weekday: number) {
  if (isToday) {
    return '#fff';
  }

  if (weekday === 0) {
    return '#dc2626';
  }

  if (weekday === 6) {
    return '#2563eb';
  }

  return '#475569';
}

function EventCalendar({ events, onSelect }: EventCalendarProps) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() }; // month: 0~11
  });

  // 날짜(yyyy-mm-dd)별 대회 묶음
  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventSummary[]>();
    for (const event of events) {
      const list = map.get(event.eventDate) ?? [];
      list.push(event);
      map.set(event.eventDate, list);
    }
    return map;
  }, [events]);

  const startWeekday = new Date(cursor.year, cursor.month, 1).getDay();
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const dateKey = (day: number) => `${cursor.year}-${pad(cursor.month + 1)}-${pad(day)}`;
  const today = todayKey();

  const prevMonth = () =>
    setCursor((c) => moveMonth(c.year, c.month, -1));
  const nextMonth = () =>
    setCursor((c) => moveMonth(c.year, c.month, 1));
  const goToday = () => {
    const now = new Date();
    setCursor({ year: now.getFullYear(), month: now.getMonth() });
  };

  return (
    <Box
      sx={{
        border: '1px solid #e8edf3',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      {/* 월 이동 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #eef2f7',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" aria-label="이전 달" onClick={prevMonth}>‹</IconButton>
          <Box sx={{ fontSize: 16, fontWeight: 800, color: '#111827', minWidth: 110, textAlign: 'center' }}>
            {cursor.year}년 {cursor.month + 1}월
          </Box>
          <IconButton size="small" aria-label="다음 달" onClick={nextMonth}>›</IconButton>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={goToday}
          sx={{ color: '#475569', borderColor: '#e2e8f0', fontWeight: 700 }}
        >
          오늘
        </Button>
      </Box>

      {/* 요일 헤더 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WEEKDAYS.map((day, idx) => (
          <Box
            key={day}
            sx={{
              py: 1,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: getWeekdayColor(idx),
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #eef2f7',
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* 날짜 셀 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((day, idx) => {
          const key = day ? dateKey(day) : `blank-${idx}`;
          const dayEvents = day ? eventsByDate.get(dateKey(day)) ?? [] : [];
          const isToday = day != null && dateKey(day) === today;
          const weekday = idx % 7;

          return (
            <Box
              key={key}
              sx={{
                minHeight: 104,
                p: 0.75,
                borderRight: weekday === 6 ? 'none' : '1px solid #f1f5f9',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: day ? '#fff' : '#fcfdfe',
              }}
            >
              {day && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 20,
                    height: 20,
                    mb: 0.5,
                    px: 0.5,
                    borderRadius: '50%',
                    fontSize: 12,
                    fontWeight: 700,
                    color: getDateTextColor(isToday, weekday),
                    backgroundColor: isToday ? '#1da1f2' : 'transparent',
                  }}
                >
                  {day}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dayEvents.map((event) => {
                  const meta = EVENT_STATUS_META[getEventStatus(event)];
                  return (
                    <Box
                      key={event.id}
                      onClick={() => onSelect(event)}
                      title={event.title}
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: meta.fg,
                        backgroundColor: meta.bg,
                        '&:hover': { filter: 'brightness(0.96)' },
                      }}
                    >
                      {event.title}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default EventCalendar;
